import type { Map as MapLibreMap } from "maplibre-gl";
import type { Feature } from "geojson";

import { AppDispatch } from "@/store";
import {
  type AnalysisConfigSnapshot,
  resetAnalysisConfigSlices,
  restoreAnalysisConfigSnapshot
} from "@/store/analysisConfigSnapshot";
import type { MapCamera } from "@/store/slice/onboardingSlice";
import type { SpatialState } from "@/store/slice/spatialSlice";
import { resetUIDataConfig, setUIDataConfig } from "@/store/slice/uiSlice";
import type { SpatialAggregationType } from "@/types/common";
import type { Dataset, DatasetData, DatasetsMap } from "@/types/dataset";
import type { GetOnboardingModulesResponse } from "@/types/onboarding";
import { getAvailableDatasets } from "@/utils/dataset";

/** Gives the BE module id for a module code, or null if the code is unknown. */
export function resolveModuleId(
  modulesResp: GetOnboardingModulesResponse,
  moduleCode: string
): string | null {
  return modulesResp.modules.find((m) => m.code === moduleCode)?.id ?? null;
}

/**
 * Saves the position of the live MapLibre camera.
 * exitOnboardingMode uses the position to move the user back to that view.
 * If no map is mounted, the function gives null and restoreMapCamera does nothing.
 */
export function captureMapCamera(map: MapLibreMap | null): MapCamera | null {
  if (!map) return null;

  const { lng, lat } = map.getCenter();
  return {
    center: [lng, lat],
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch()
  };
}

/**
 * Puts a saved camera position on the live map.
 * The function does nothing if the map or the camera is absent.
 */
export function restoreMapCamera(
  map: MapLibreMap | null,
  camera: MapCamera | null
): void {
  if (!map || !camera) return;

  map.jumpTo({
    center: camera.center,
    zoom: camera.zoom,
    bearing: camera.bearing,
    pitch: camera.pitch
  });
}

const addUniqueFeature = (
  featuresByKey: Map<string, Feature>,
  key: string,
  feature: Feature | undefined
): void => {
  if (!feature?.geometry) return;

  const featureKey =
    feature.id === undefined || feature.id === null ? key : String(feature.id);

  featuresByKey.set(featureKey, feature);
};

export function getSnapshotDrawnPolygonFeatures(
  snapshot: AnalysisConfigSnapshot
): Feature[] {
  const spatial = snapshot.spatial as Partial<SpatialState> | null | undefined;
  const featuresByKey = new Map<string, Feature>();

  Object.entries(spatial?.polygonSettings ?? {}).forEach(([key, value]) => {
    addUniqueFeature(featuresByKey, key, value?.polygon as Feature | undefined);
  });

  Object.entries(spatial?.polygonSettingsTemp ?? {}).forEach(([key, value]) => {
    addUniqueFeature(featuresByKey, key, value?.polygon as Feature | undefined);
  });

  Object.entries(spatial?.drawnPolygon ?? {}).forEach(([key, feature]) => {
    addUniqueFeature(featuresByKey, key, feature as Feature | undefined);
  });

  return Array.from(featuresByKey.values());
}

/**
 * Draws the polygons again on the mapbox-gl-draw control.
 * The enter flow cleared the control, and a Redux restore does not draw again.
 * Give the snapshot features directly. Then the restore does not wait for a
 * selector refresh, and the temporary polygons come back with the saved ones.
 */
export function restoreDrawnPolygons(
  deleteAllFeatures: () => void,
  renderDrawPolygon: (features?: Feature[]) => void,
  features?: Feature[]
): void {
  // First remove the onboarding features. Then draw the user polygons.
  deleteAllFeatures();
  renderDrawPolygon(features);
}

/**
 * Sets each snapshot slice to its initial value for a clean onboarding start.
 * auth and analysisJob do not change. analysisJob keeps the real BE jobs.
 */
export function resetOnboardingSlices(dispatch: AppDispatch): void {
  resetAnalysisConfigSlices(dispatch);
  dispatch(resetUIDataConfig());
}

/**
 * Removes each dataset source and layer that the user had before onboarding.
 * resetOnboardingSlices clears the Redux dataset slice, but MapLibre sources and
 * layers stay after a Redux reset. Thus the real datasets of the user stay on the
 * clean onboarding map. The removal is the same as in
 * useMapLayers.handleResetDatasetToHistoryDataset, but for each available dataset.
 */
export function resetOnboardingMapLayers(
  datasets: DatasetsMap,
  handleRemoveSourceLayer: (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => void
): void {
  Object.entries(getAvailableDatasets(datasets)).forEach(
    ([datasetId, dataset]) => {
      handleRemoveSourceLayer(datasetId, dataset.properties.spatialType);
    }
  );
}

/**
 * Draws the dataset sources and layers of the user again.
 * This function is the counterpart of resetOnboardingMapLayers.
 * restoreOnboardingSlices puts the dataset slice back in Redux, but MapLibre
 * sources and layers are not in Redux. Thus the datasets of the user stay off the
 * map. Draw from the snapshot, then set the stack order again.
 *
 * The dataset slice of the snapshot has the type `unknown`. The function casts it
 * to the fields that the layer handlers need. If the snapshot has no dataset
 * field, the function only clears the onboarding layers.
 */
export function restoreOnboardingMapLayers(
  snapshotDataset: unknown,
  currentDatasets: DatasetsMap,
  handleRemoveSourceLayer: (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => void,
  handleResetLayerData: (
    datasets: { [id: string]: Dataset },
    rawDatasets: { [id: string]: DatasetData }
  ) => void,
  handleResetLayerOrder: (layerOrders: string[]) => void
): void {
  const dataset = snapshotDataset as
    | {
        datasets: DatasetsMap;
        rawDatasets: { [id: string]: DatasetData };
        visualizationOrder: string[];
      }
    | null
    | undefined;

  // First remove the layers that onboarding painted, for example the
  // explore-dataset fixture. Then draw the saved datasets of the user again.
  resetOnboardingMapLayers(currentDatasets, handleRemoveSourceLayer);

  if (!dataset) return;

  // Draw before you set the order. handleResetLayerOrder moves the layers that
  // are in the style. Thus the layers must be on the map first.
  handleResetLayerData(dataset.datasets, dataset.rawDatasets);
  handleResetLayerOrder(dataset.visualizationOrder ?? []);
}

/**
 * Puts each parked slice back into Redux when onboarding stops.
 * The slice set and the order are the same as in resetOnboardingSlices, and
 * analysisJob does not change. The two halves come from different places: the
 * shared analysis config from `state.analysisConfigSnapshot`, and `ui` from the
 * session of the tour. Thus they are separate parameters. `parkedUi` has the type
 * `unknown`. The function casts it to the payload type of the action.
 */
export function restoreOnboardingSlices(
  dispatch: AppDispatch,
  snapshot: AnalysisConfigSnapshot,
  parkedUi: unknown
): void {
  restoreAnalysisConfigSnapshot(dispatch, snapshot);
  dispatch(setUIDataConfig(parkedUi as Parameters<typeof setUIDataConfig>[0]));
}
